import { appNewRecordPath } from '@/runtime/entityRoutes';

export type CommandGroup = 'action' | 'navigate';

export interface Command {
  id: string;
  group: CommandGroup;
  label: string;
  hint?: string;
  icon?: string;
  run: () => void;
}

interface Entity {
  name: string;
  table_name: string;
  synonym?: string;
}

interface Report {
  id: string;
  name: string;
  route: string;
  synonym?: string;
}

export interface RegistryInput {
  entities: {
    catalogs: Entity[];
    documents: Entity[];
  };
  reports: Report[];
  onNavigate: (path: string) => void;
  onToggleTheme: () => void;
  onSwitchConnection: () => void;
}

export function buildCommands(input: RegistryInput): Command[] {
  const commands: Command[] = [];

  // Navigate commands for catalogs
  input.entities.catalogs.forEach(catalog => {
    commands.push({
      id: `nav:catalog:${catalog.table_name}`,
      group: 'navigate',
      label: catalog.synonym ?? catalog.name,
      run: () => input.onNavigate(`/app/${catalog.name}`),
    });
  });

  // Navigate commands for documents
  input.entities.documents.forEach(document => {
    commands.push({
      id: `nav:document:${document.table_name}`,
      group: 'navigate',
      label: document.synonym ?? document.name,
      run: () => input.onNavigate(`/app/${document.name}`),
    });
  });

  // Navigate commands for reports
  input.reports.forEach(report => {
    commands.push({
      id: `nav:report:${report.id}`,
      group: 'navigate',
      label: report.synonym ?? report.name,
      run: () => input.onNavigate(report.route),
    });
  });

  // Static action commands — pushed BEFORE the per-entity "New X" actions so
  // they survive the empty-state TOP_ACTIONS_WHEN_EMPTY slice in
  // CommandPalette (which preserves this array's relative order for the tied
  // neutral-score ranking of an empty query).
  commands.push({
    id: 'action:toggle-theme',
    group: 'action',
    label: 'Toggle theme',
    run: () => input.onToggleTheme(),
  });

  commands.push({
    id: 'action:switch-connection',
    group: 'action',
    label: 'Switch connection…',
    run: () => input.onSwitchConnection(),
  });

  // Action commands for creating new catalogs — real create deep-link via the
  // unified /app/:objectName/new runtime route (RuntimeFormPage treats
  // recordId 'new' as create).
  input.entities.catalogs.forEach(catalog => {
    commands.push({
      id: `new:catalog:${catalog.table_name}`,
      group: 'action',
      label: `New ${catalog.synonym ?? catalog.name}`,
      run: () => input.onNavigate(appNewRecordPath(catalog.name)),
    });
  });

  // Action commands for creating new documents
  input.entities.documents.forEach(document => {
    commands.push({
      id: `new:document:${document.table_name}`,
      group: 'action',
      label: `New ${document.synonym ?? document.name}`,
      run: () => input.onNavigate(appNewRecordPath(document.name)),
    });
  });

  return commands;
}
